
//WebSocketManager doing below tasks
// WebSocketManager handles WebSocket connections
//initialize all events of web-sockets and fire event-handlers correspondingly
//save all web-sockets in dictionary (key-value) (Map)
//open,close web-sockets base on their key (ws url)
//we can add more generic tasks in it...

/** Map key ignores the access token so a silent refresh does not miss the open socket. */
function connectionKey(url) {
    return String(url || '')
        .replace(/([?&])token=[^&]*/g, '$1')
        .replace(/[?&]$/, '')
        .replace(/\?&/g, '?')
        .replace(/&&/g, '&');
}

/** Rebuild the upgrade URL with the access token currently in localStorage. */
function withCurrentAccessToken(url) {
    const base = connectionKey(url);
    let token = null;
    try {
        token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    } catch (_) {
        token = null;
    }
    if (!token) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}token=accessToken=${token}`;
}

class WebSocketManager {
    constructor() {
        // Dictionary (key-value) of all WebSocket connections
        this.TOTAL_ALLOWED_CONNECTIONS = 1; // Allowed number of connections at a time to maintain the load
        this.connections = new Map();
        this.sendQueue = new Map();
        this.calledFirstTime = true; // Flag to set first time called or not
        this.openedConnectionsCount = 0; // Counter for opened connections
        this.disconnectAllCalled = false; // Flag to track whether disconnectAll has been called   
        this.newConnectionQueue = new Map(); // New connection queue while disconnect all in progress
        this.reconnectInterval = 5000; // Reconnection interval in milliseconds
        this.eventHandlersMap = new Map(); // To store event handlers for reconnection
        this.pendingConnections = new WeakMap();
        this.connectionTimers = new WeakMap();
    }

    rejectPendingConnection(ws, error) {
        clearTimeout(this.connectionTimers.get(ws));
        this.connectionTimers.delete(ws);
        const reject = this.pendingConnections.get(ws);
        this.pendingConnections.delete(ws);
        if (reject) reject(error);
    }

    isSelectionMessage(data) {
        try { return JSON.parse(data)?.type === 'selectedSymbolChange'; }
        catch { return false; }
    }

    supersedeQueuedSelection(url) {
        const key = connectionKey(url);
        const queue = this.sendQueue.get(key);
        if (!queue) return;
        const retained = [];
        for (const item of queue) {
            if (this.isSelectionMessage(item.data)) item.resolve();
            else retained.push(item);
        }
        if (retained.length) this.sendQueue.set(key, retained);
        else this.sendQueue.delete(key);
    }

    rejectQueuedSends(url, error) {
        const key = connectionKey(url);
        const queue = this.sendQueue.get(key) || [];
        this.sendQueue.delete(key);
        queue.forEach(item => item.reject(error));
    }

    flushQueuedSends(url, ws) {
        const key = connectionKey(url);
        if (this.connections.get(key) !== ws || ws.readyState !== WebSocket.OPEN) return;
        const queue = this.sendQueue.get(key) || [];
        this.sendQueue.delete(key);
        for (const item of queue) {
            try { ws.send(item.data); item.resolve(); }
            catch (error) { item.reject(error); }
        }
    }

    /**
     * Connects to a WebSocket and handles events.
     * Closes existing connection before establishing a new one.
     * @param {string} url - WebSocket URL.
     * @param {Object} eventHandlers - Object with event handlers (onOpen, onMessage, onError, onClose).
     * @param {string} dispatchEventName - Name of the event to dispatch.
     * @returns {Promise<WebSocket>} - Promise resolving to the WebSocket instance.
     */
    async connect(url, eventHandlers, dispatchEventName = "") {
        return new Promise(async (resolve, reject) => {
            const key = connectionKey(url);
            // Save the event handlers for reconnection (key has no access token)
            this.eventHandlersMap.set(key, { eventHandlers, dispatchEventName, url });

            // If not called first time 
            if (!this.calledFirstTime) {
                // If disconnectAll has not been called yet, call it
                if (!this.disconnectAllCalled) {
                    this.disconnectAllCalled = true;
                    await this.disconnectAll();

                    // Now that disconnectAll has resolved, establish the new connection
                    await this.establishConnection(url, eventHandlers, dispatchEventName, resolve, reject);

                    this.newConnectionQueue.forEach(async (connection) => {
                        await this.establishConnection(connection.url, connection.eventHandlers, connection.dispatchEventName, resolve, reject);
                    });

                    this.newConnectionQueue.clear();
                } else {
                    // Add new connection in queue while disconnect all in progress
                    // Once disconnect all resolved then open new connection in queue
                    this.newConnectionQueue.set(key, { url, eventHandlers, dispatchEventName });
                }
            } else {
                // Called first time, proceed to establish the connection                
                await this.establishConnection(url, eventHandlers, dispatchEventName, resolve, reject);
            }
        });
    }

    /**
     * Internal method to establish a connection after disconnectAll.
     */
    async establishConnection(url, eventHandlers, dispatchEventName, resolve, reject) {
        const key = connectionKey(url);
        const ws = new WebSocket(url);
        this.pendingConnections.set(ws, reject);

        ws.onopen = (event) => {
            if (this.connections.get(key) !== ws) return;
            clearTimeout(this.connectionTimers.get(ws));
            this.connectionTimers.delete(ws);
            this.pendingConnections.delete(ws);
            if (eventHandlers.onOpen) {
                eventHandlers.onOpen(event);
            }
            // onOpen publishes the current selection and supersedes an older
            // queued selection before pagination/other messages are drained.
            this.flushQueuedSends(url, ws);
            resolve(ws); // Resolve the Promise with the WebSocket instance.

            // Increment the counter for opened connections
            if (this.calledFirstTime)
                this.openedConnectionsCount++;

            // Check if all connections are opened
            if (this.calledFirstTime && this.openedConnectionsCount === this.TOTAL_ALLOWED_CONNECTIONS) {
                this.openedConnectionsCount = 0;
                this.calledFirstTime = false;
            }
        };

        ws.onmessage = (event) => {
            if (this.connections.get(key) !== ws) return;
            if (eventHandlers.onMessage) {
                eventHandlers.onMessage(event, dispatchEventName);
            }
        };

        ws.onerror = (event) => {
            if (this.connections.get(key) === ws) this.rejectQueuedSends(key, new Error('WebSocket connection failed.'));
            this.rejectPendingConnection(ws, event);
            if (eventHandlers.onError) {
                eventHandlers.onError(event);
            }
            reject(event); // Reject the Promise on WebSocket error.
        };

        ws.onclose = (event) => {
            this.rejectPendingConnection(ws, new Error('WebSocket closed before opening.'));
            if (this.connections.get(key) !== ws) return;
            this.rejectQueuedSends(key, new Error('WebSocket closed before queued data could be sent.'));
            if (eventHandlers.onClose) {
                eventHandlers.onClose(event);
            }
            const key = connectionKey(url);
            if (this.connections.get(key) === ws) {
                this.connections.delete(key);
            }

            // Check if all connections are closed
            if (this.connections.size === 0) {
                // Reset the flag when all connections are closed
                this.disconnectAllCalled = false;
            }

            // Attempt to reconnect
            // this.reconnect(url);
            this.urlValue = url;
        };

        this.connections.set(key, ws);
    }

    hasOpenConnection() {
        for (const ws of this.connections.values()) {
            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Reconnects to a WebSocket after it has been closed.
     * @param {string} url - WebSocket URL.
     */
    async reconnect(url) {
        setTimeout(() => {
            const connectionDetails = this.eventHandlersMap.get(connectionKey(url));
            if (connectionDetails) {
                const liveUrl = withCurrentAccessToken(connectionDetails.url || url);
                this.connect(liveUrl, connectionDetails.eventHandlers, connectionDetails.dispatchEventName)
                    .then(() => console.log(`Reconnected to ${connectionKey(liveUrl)}`))
                    .catch(err => console.error(`Failed to reconnect to ${connectionKey(liveUrl)}`, err));
            }
        }, this.reconnectInterval);
    }

    async reconnectHandler() {
        return new Promise(async (resolve, reject) => {
            const url = this.urlValue;
            const connectionDetails = this.eventHandlersMap.get(connectionKey(url));
            if (connectionDetails) {
                try {
                    const liveUrl = withCurrentAccessToken(connectionDetails.url || url);
                    const ws = await this.connect(liveUrl, connectionDetails.eventHandlers, connectionDetails.dispatchEventName);
                    console.log(`Reconnected to ${connectionKey(liveUrl)}`);
                    resolve(ws);
                } catch (err) {
                    console.error(`Failed to reconnect`, err);
                    reject(err);
                }
            } else {
                reject(new Error("Connection details not found."));
            }
        });
    }

    // Do not wait for a dead peer's close handshake before reopening the feed.
    async restartCurrent() {
        const key = connectionKey(this.connections.keys().next().value || this.urlValue);
        const details = this.eventHandlersMap.get(key);
        if (!details) return;
        const ws = this.connections.get(key);
        if (ws) {
            this.connections.delete(key);
            this.rejectPendingConnection(ws, new Error('Replacing unresponsive WebSocket.'));
            this.rejectQueuedSends(key, new Error('Replacing unresponsive WebSocket.'));
            ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
            ws.close();
        }
        // Publish loss before open so chart recovery also runs for silent sockets.
        details.eventHandlers.onClose?.({ code: 1006, reason: 'Feed recovery' });
        const liveUrl = withCurrentAccessToken(details.url || this.urlValue || key);
        return this.connect(liveUrl, details.eventHandlers, details.dispatchEventName);
    }

    /**
     * Disconnects from a WebSocket.
     * @param {string} url - WebSocket URL.
     * @returns {Promise} - Promise that resolves when the WebSocket is closed.
     */
    async disconnect(url) {
        return new Promise(async (resolve, reject) => {
            const key = connectionKey(url);
            const ws = this.connections.get(key);
            if (ws) {
                this.rejectPendingConnection(ws, new Error('WebSocket disconnected before opening.'));
                ws.onclose = () => {
                    this.connections.delete(key);
                    resolve(); // Resolve the disconnect Promise
                };

                await ws.close();
            } else {
                resolve(); // Resolve the disconnect Promise if there is no existing connection
            }
        });
    }

    // Send data to server from client
    async sendData(url, data) {
        return new Promise((resolve, reject) => {
            const key = connectionKey(url);
            const ws = this.connections.get(key);

            // The symbol list asks for quotes as soon as it mounts, often before
            // the platform socket exists. Hold that request and send it on open
            // so the first screen does not stay blank until the user scrolls.
            if (!ws || ws.readyState === WebSocket.CONNECTING) {
                if (!this.sendQueue.has(key)) {
                    this.sendQueue.set(key, []);
                }
                this.sendQueue.get(key).push({ data, resolve, reject });
                return;
            }
    
            if (ws.readyState === WebSocket.OPEN) {
                if (this.isSelectionMessage(data)) this.supersedeQueuedSelection(url);
                ws.send(data);
                resolve();
                return;
            }
    
            reject(new Error("WebSocket is not open."));
        });
    }

    /**
     * Disconnects from all WebSocket connections.
     * @returns {Promise} - Promise that resolves when all connections are closed.
     */
    async disconnectAll() {
        return new Promise(async (resolve, reject) => {
            for (const url of this.sendQueue.keys()) {
                this.rejectQueuedSends(url, new Error('WebSocket disconnected before queued data could be sent.'));
            }
            const totalConnections = this.connections.size;
            let closedConnections = 0;

            if (totalConnections === 0) {
                // Reset the flag when disconnectAll is called with no existing connections
                this.disconnectAllCalled = false;
                resolve();
                return;
            }

            const closePromises = [];

            this.connections.forEach((ws, url) => {
                this.rejectPendingConnection(ws, new Error('WebSocket disconnected before opening.'));
                const closePromise = new Promise(async (closeResolve) => {
                    this.urlValue = url;
                    ws.onclose = async () => {
                        // Ensure that the onclose event is awaited before resolving the closePromise
                        await new Promise((innerResolve) => setTimeout(innerResolve, 0));
                        closedConnections++;

                        if (closedConnections === totalConnections) {
                            // All connections are closed
                            this.connections.clear();
                            // Reset the flag when all connections are closed
                            this.disconnectAllCalled = false;
                            resolve();
                        }

                        closeResolve(); // Resolve the closePromise
                    };
                });

                closePromises.push(closePromise);

                // Close the WebSocket
                ws.close();
            });

            // Wait for all closePromises to resolve
            await Promise.all(closePromises);
        });
    }

    // Add more methods to handle other WebSocket events if needed
}

// Export WebSocketManager instance to maintain all connections globally
export const webSocketManagerGlobal = new WebSocketManager();
